import { supabase } from "@supabaseClient/supabaseClient";
import { EntityImagesClient } from "@Clients/EntityImagesClient";

export interface IProduct {
  id: string;
  name: string;
  sku: string;
  description?: string;
  price: number | null;
  organization_id: string;
  metadata?: Record<string, string | number | boolean | null | undefined>;
  created_at: string;
  updated_at: string;
}

export type NewProduct = Omit<IProduct, "id" | "created_at" | "updated_at">;
export type UpdateProduct = Omit<IProduct, "created_at" | "updated_at">;

export const productsClient = {
  async list(organizationId: string): Promise<IProduct[]> {
    const { data } = await supabase
      .from("products")
      .select("*")
      .eq("organization_id", organizationId)
      .throwOnError();
    return (data ?? []) as IProduct[];
  },

  async get(id: string): Promise<IProduct> {
    const { data } = await supabase
      .from("products")
      .select("*")
      .eq("id", id)
      .single()
      .throwOnError();
    return data as IProduct;
  },

  async create(input: NewProduct): Promise<IProduct> {
    const { data } = await supabase
      .from("products")
      .insert(input)
      .select()
      .single()
      .throwOnError();
    return data as IProduct;
  },

  async update(input: UpdateProduct): Promise<IProduct> {
    const { data } = await supabase
      .from("products")
      .update(input)
      .eq("id", input.id)
      .select()
      .single()
      .throwOnError();
    return data as IProduct;
  },

  async remove(id: string): Promise<void> {
    await supabase.from("products").delete().eq("id", id).throwOnError();
    // The product row is gone; clean up its polymorphic image attachments
    // (rows + Storage bytes) best-effort. A cleanup hiccup shouldn't surface as
    // a delete failure, since the user-visible action already succeeded.
    try {
      await EntityImagesClient.removeAllForEntity("product", id);
    } catch {
      // Leave any orphaned images rather than failing a completed delete.
    }
  },
};
